import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Loader } from "@/components/Atoms";
import { Timer, Coins, ExternalLink, Lock } from "lucide-react";

export function EconomySettings() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  const load = () =>
    api.get("/admin/economy/settings")
      .then(({ data }) => setList(data.settings || []))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const save = async (key, value) => {
    try {
      const { data } = await api.put(`/admin/economy/settings/${key}?value=${encodeURIComponent(value)}`);
      setList((l) => l.map((s) => (s.key === key ? { ...s, value: data.value } : s)));
      toast.success(
        data.mirrored?.length
          ? `Saved — Discord (${data.mirrored.join(", ")}) kept in sync`
          : "Saved",
      );
    } catch (e) { toast.error(apiError(e)); }
  };

  const groups = useMemo(() => {
    const out = [];
    list.forEach((s) => {
      const g = out.find((x) => x.name === s.group);
      if (g) g.rows.push(s); else out.push({ name: s.group, rows: [s] });
    });
    return out;
  }, [list]);

  if (loading) return <Loader />;

  return (
    <div className="space-y-5 fade-up" data-testid="admin-economy-settings">
      <div className="glass-card p-3 text-xs text-[var(--muted)] space-y-1.5">
        <div className="flex items-center gap-2" data-testid="economy-cooldowns-notice">
          <Timer size={14} className="text-[var(--teal)]" />
          <span>
            Cooldowns live in{" "}
            <Link to="/admin/cooldowns" className="underline hover:text-[var(--bone)]">Admin → Cooldowns</Link>.
          </span>
        </div>
        <div className="flex items-center gap-2" data-testid="economy-builder-pricing-notice">
          <Coins size={14} className="text-amber-300" />
          <span>
            Pal Builder coin costs live in{" "}
            <Link to="/admin/builder-pricing" className="underline hover:text-[var(--bone)]">Admin → Builder Pricing</Link>{" "}
            — they're validated there, so they're hidden from this raw list.
          </span>
        </div>
      </div>

      {groups.map((g) => (
        <section key={g.name} data-testid={`economy-group-${g.name.replace(/\s+/g, "-").toLowerCase()}`}>
          <h3 className="text-xs uppercase tracking-wider text-[var(--muted)] mb-2">{g.name}</h3>
          <div className="space-y-2">
            {g.rows.map((s) => <SettingRow key={s.key} setting={s} onSave={save} />)}
          </div>
        </section>
      ))}

      {list.length === 0 && <p className="text-[var(--muted)]">No settings.</p>}
    </div>
  );
}

function SettingRow({ setting, onSave }) {
  const [val, setVal] = useState(setting.value ?? "");
  useEffect(() => { setVal(setting.value ?? ""); }, [setting.value]);

  const locked = setting.type === "readonly" || !!setting.managed_by;
  const dirty = String(val) !== String(setting.value ?? "");
  const isJson = setting.type === "json";

  return (
    <div className="glass-card p-3" data-testid={`economy-setting-${setting.key}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-[180px] flex-1">
          <div className="text-sm text-[var(--bone)] flex items-center gap-1.5">
            {locked && <Lock size={11} className="text-[var(--muted)]" />}
            {setting.label}
          </div>
          <div className="text-[10px] font-mono text-[var(--muted)] mt-0.5">{setting.key}</div>
          {setting.hint && (
            <div className="text-[11px] text-[var(--muted)] mt-1 leading-snug">{setting.hint}</div>
          )}
          {setting.mirror?.length > 0 && (
            <div className="text-[10px] text-[var(--teal)] mt-1">
              Also written to Discord's {setting.mirror.join(", ")} on save
            </div>
          )}
          {setting.managed_by && (
            <Link
              to={setting.managed_by.path}
              className="text-[11px] text-[var(--teal)] underline inline-flex items-center gap-1 mt-1"
              data-testid={`economy-setting-link-${setting.key}`}
            >
              {setting.managed_by.label} <ExternalLink size={10} />
            </Link>
          )}
        </div>

        <div className="flex items-center gap-2 flex-1 min-w-[240px] justify-end">
          {isJson ? (
            <textarea
              className="input flex-1 font-mono text-[11px] h-20"
              value={val}
              disabled={locked}
              onChange={(e) => setVal(e.target.value)}
              data-testid={`economy-input-${setting.key}`}
            />
          ) : (
            <input
              className="input flex-1"
              type={setting.type === "int" ? "number" : setting.type === "percent" ? "number" : "text"}
              step={setting.type === "percent" ? "0.01" : undefined}
              min={setting.min}
              max={setting.max}
              value={val}
              disabled={locked}
              onChange={(e) => setVal(e.target.value)}
              data-testid={`economy-input-${setting.key}`}
            />
          )}
          {!locked && (
            <button
              onClick={() => onSave(setting.key, val)}
              disabled={!dirty}
              className="btn btn-sm"
              data-testid={`save-setting-${setting.key}`}
            >
              {dirty ? "Save" : "Saved"}
            </button>
          )}
        </div>
      </div>
      {setting.type === "percent" && Number.isFinite(Number(val)) && (
        <div className="text-[10px] text-[var(--muted)] mt-1">= {(Number(val) * 100).toFixed(2)}%</div>
      )}
    </div>
  );
}
